// src/pages/Invoices.js
import React, { useEffect, useState } from "react";
import api from "../services/api.js";
import Pagination from "../components/Customer/Pagination.js";
import ManualInvoiceModal from "../components/Invoicing/CreateManualInvoice.js";

export default function Invoices({ role }) {
  const token = localStorage.getItem("token");

  const [invoices, setInvoices] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [exporting, setExporting] = useState(false);

  const [params, setParams] = useState({
    page: 1,
    limit: 100,
    search: "",
    status: "",
    paymentmethod: "",
    date_from: "",
    date_to: "",
    sortBy: "date",
    sortOrder: "DESC",
  });

  const fetchInvoices = async () => {
    try {
      setLoading(true);

      const { data } = await api.get("/invoices", {
        params,
        headers: {
          Authorization: token,
        },
      });

      setInvoices(data.data || []);
      setMeta(data.meta || {});
    } catch (err) {
      console.error(
        "Fetch invoices failed:",
        err.response?.data || err.message
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line
  }, [
    params.page,
    params.limit,
    params.search,
    params.status,
    params.paymentmethod,
    params.date_from,
    params.date_to,
    params.sortBy,
    params.sortOrder,
  ]);

  // Helper: Format Date
  const formatDate = (dateString) => {
    if (!dateString || dateString === "No order yet") return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportAllInvoicesCSV = async () => {
    try {
      setExporting(true);

      let allInvoices = [];
      let page = 1;
      let totalPages = 1;

      do {
        const { data } = await api.get("/invoices", {
          params: {
            ...params,
            page,
            limit: 100,
          },
          headers: {
            Authorization: token,
          },
        });

        const items = data.data || [];
        const metaInfo = data.meta || {};

        allInvoices = allInvoices.concat(items);
        totalPages = metaInfo.totalPages || 1;
        page++;
      } while (page <= totalPages);

      if (!allInvoices.length) return;

      // Header CSV (explicit & finance-safe)
      const headers = [
        "Invoice ID",
        "Customer Name",
        "Customer ID",
        "Invoice Date",
        "Due Date",
        "Total",
        "Status",
        "Payment Method",
      ];

      const rows = allInvoices.map((inv) => [
        inv.id,
        `${inv.firstname} ${inv.lastname}`,
        inv.cust_id,
        formatDate(inv.date),
        formatDate(inv.duedate),
        inv.total,
        inv.status,
        inv.paymentmethod ?? "-",
      ]);

      const csv = [
        headers.join(","),
        ...rows.map((r) => r.map((v) => JSON.stringify(v ?? "")).join(",")),
      ].join("\n");

      const blob = new Blob([csv], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `invoices-all-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export CSV failed:", err);
      alert("Gagal export CSV");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-5">
      <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
        Invoices
      </h2>

      {/* FILTER */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search cust_id / invoice / name"
          value={params.search}
          onChange={(e) =>
            setParams({ ...params, page: 1, search: e.target.value })
          }
          className="px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
        />

        <select
          value={params.status}
          onChange={(e) =>
            setParams({ ...params, page: 1, status: e.target.value })
          }
          className="px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
        >
          <option value="">All Status</option>
          <option value="Paid">Paid</option>
          <option value="Unpaid">Unpaid</option>
          <option value="Cancelled">Cancelled</option>
        </select>

        <select
          value={params.paymentmethod}
          onChange={(e) =>
            setParams({ ...params, page: 1, paymentmethod: e.target.value })
          }
          className="px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
        >
          <option value="">All Payment</option>
          <option value="Duitku">Duitku</option>
          <option value="Transfer">Transfer</option>
        </select>

        <input
          type="date"
          value={params.date_from}
          onChange={(e) =>
            setParams({ ...params, page: 1, date_from: e.target.value })
          }
          className="px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
        />

        <input
          type="date"
          value={params.date_to}
          onChange={(e) =>
            setParams({ ...params, page: 1, date_to: e.target.value })
          }
          className="px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
        />

        <select
          value={params.sortBy}
          onChange={(e) =>
            setParams({ ...params, page: 1, sortBy: e.target.value })
          }
          className="px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
        >
          <option value="date">Invoice Date</option>
          <option value="duedate">Due Date</option>
          <option value="datepaid">Date Paid</option>
          <option value="total">Total</option>
          <option value="status">Status</option>
          <option value="created_at_sync">Created</option>
        </select>

        <select
          value={params.sortOrder}
          onChange={(e) =>
            setParams({ ...params, page: 1, sortOrder: e.target.value })
          }
          className="px-3 py-2 rounded-lg border bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
        >
          <option value="DESC">DESC</option>
          <option value="ASC">ASC</option>
        </select>
      </div>

      {/* CREATE INVOICE */}
      <div className="flex justify-end gap-3 mb-4">
        <button
          onClick={exportAllInvoicesCSV}
          disabled={exporting}
          className="bg-green-600 text-white px-4 py-2 rounded shadow disabled:opacity-60"
        >
          {exporting ? "Exporting..." : "Export ALL CSV"}
        </button>

        {role === "billing" && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded shadow"
          >
            Create Invoice
          </button>
        )}

        <ManualInvoiceModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            console.log("Invoice created, refreshing data...");
          }}
        />
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded-xl shadow bg-white dark:bg-gray-800">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left">Invoice</th>
              <th className="px-4 py-3 text-left">Invoice Numb</th>
              <th className="px-4 py-3 text-left">Customer</th>
              <th className="px-4 py-3 text-left">Cust ID</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Due-Date</th>
              <th className="px-4 py-3 text-left">Total</th>
              <th className="px-4 py-3 text-left">Status</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : invoices.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-6 text-center text-gray-500">
                  No data found
                </td>
              </tr>
            ) : (
              invoices.map((inv) => (
                <tr
                  key={inv.id}
                  onClick={() =>
                    window.open(`/invoice-detail/${inv.id}`, "_blank")
                  }
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  <td className="px-4 py-3 font-medium">{inv.id}</td>
                  <td className="px-4 py-3 font-medium">{inv.invoicenum}</td>
                  <td className="px-4 py-3">
                    {inv.firstname} {inv.lastname}
                  </td>
                  <td className="px-4 py-3">{inv.cust_id}</td>
                  <td className="px-4 py-3">{formatDate(inv.date)}</td>
                  <td className="px-4 py-3">{formatDate(inv.duedate)}</td>
                  <td className="px-4 py-3">
                    Rp {Number(inv.total).toLocaleString("id-ID")}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold
                        ${
                          inv.status === "Paid"
                            ? "bg-green-600 text-white"
                            : inv.status === "Unpaid"
                            ? "bg-yellow-500 text-black"
                            : "bg-red-600 text-white"
                        }`}
                    >
                      {inv.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {meta.totalPages > 1 && (
        <Pagination
          currentPage={meta.page}
          totalPages={Math.min(meta.totalPages, 100)}
          onPageChange={(page) => setParams({ ...params, page })}
        />
      )}
    </div>
  );
}
